// src/components/product/ProductCard.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { formatCurrency } from '../../utils/helpers';
import AddToCartModal from './AddToCartModal';
import useSnapScroller from '../../hooks/useSnapScroller';
import useMediaQuery from '../../hooks/useMediaQuery';

/**
 * Product Card Component
 * Displays product in grid:
 * - Product image
 * - Name and college
 * - Price with discount
 * - "Add to Cart" button (opens modal)
 */
const ProductCard = ({ product }) => {
  const [showModal, setShowModal] = useState(false);

  const imageUrls = product.imageUrls?.length
    ? product.imageUrls
    : ['/placeholder-product.png'];
  const hasMultipleImages = imageUrls.length > 1;

  // Render only the active layout branch. Both branches always in the DOM
  // (one hidden via `display:none`) still download their images in Chrome,
  // so we mount just one — matching Tailwind's `lg` breakpoint (1024px).
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  // State to track which image is currently selected
  const [activeImage, setActiveImage] = useState(imageUrls[0]);
  const { scrollerRef, activeSlide, handleScroll, scrollToSlide } =
    useSnapScroller(imageUrls.length);

  // Mobile: only mount the first slide until the user interacts, so 680 cards
  // don't each fetch/mount every product image on the initial page load.
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const mobileSlides = mobileExpanded ? imageUrls : imageUrls.slice(0, 1);
  const pendingSlideRef = useRef(null);

  useEffect(() => {
    if (mobileExpanded && pendingSlideRef.current != null) {
      scrollToSlide(pendingSlideRef.current);
      pendingSlideRef.current = null;
    }
  }, [mobileExpanded]);

  const handleDotClick = (index) => {
    if (mobileExpanded) {
      scrollToSlide(index);
    } else {
      // Mount the full strip first, then scroll once the slide exists (effect above).
      pendingSlideRef.current = index;
      setMobileExpanded(true);
    }
  };

  // Calculate discount badge
  const hasDiscount = product.discount > 0;
  const totalStock = product.availableSizes?.reduce(
  (sum, ps) => sum + (ps.stockQuantity || 0),
  0
);

  
  return (
    <>
      <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300 group">
        {/* Product Image */}
        <div className="block relative">
          {/* Mobile - swipe between images */}
          {!isDesktop && (
          <div className="lg:hidden">
            <div
              ref={scrollerRef}
              onScroll={handleScroll}
              onTouchStart={() => setMobileExpanded(true)}
              className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide"
            >
              {mobileSlides.map((url, index) => (
                <Link
                  key={index}
                  to={`/products/${product.id}`}
                  className="w-full flex-shrink-0 snap-center aspect-square bg-gray-100"
                >
                  <img
                    src={url}
                    alt={`${product.name} ${index + 1}`}
                    className="w-full h-full object-cover"
                    width={400}
                    height={400}
                    loading="lazy"
                    decoding="async"
                    draggable={false}
                  />
                </Link>
              ))}
            </div>

            {hasMultipleImages && (
              <div className="absolute bottom-2 inset-x-0 flex justify-center">
                {/* Tinted pill so the dots stay visible on light and dark photos */}
                <div className="flex items-center gap-1.5 rounded-full bg-black/30 px-2 py-1 backdrop-blur-sm">
                  {imageUrls.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => handleDotClick(index)}
                      aria-label={`Go to image ${index + 1}`}
                      className={`
                        h-1.5 rounded-full transition-all
                        ${activeSlide === index ? 'w-4 bg-white' : 'w-1.5 bg-white/50'}
                      `}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
          )}

          {/* Desktop - hover/click thumbnails */}
          {isDesktop && (
          <div className="hidden lg:block">
            <Link to={`/products/${product.id}`} className="block aspect-square overflow-hidden bg-gray-100">
              <img
                  src={activeImage}
                  alt={product.name}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  width={400}
                  height={400}
                  loading="lazy"
                  decoding="async"
                />
            </Link>

            {/* Thumbnail Overlay (Visible on Hover for better UX) */}
            {hasMultipleImages && (
              <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-2 px-2">
                {imageUrls.map((url, index) => (
                  <button
                    key={index}
                    onMouseEnter={() => setActiveImage(url)} // Changes image on hover
                    onClick={(e) => {
                      e.preventDefault(); // Prevents navigating to product page
                      setActiveImage(url);
                    }}
                    className={`w-8 h-8 rounded-md border-2 overflow-hidden bg-white transition-all
                      ${activeImage === url ? 'border-blue-600 scale-110' : 'border-transparent opacity-80 hover:opacity-100'}`}
                  >
                    <img src={url} alt="" className="w-full h-full object-cover" width={32} height={32} loading="lazy" decoding="async" />
                  </button>
                ))}
              </div>
            )}
          </div>
          )}

          {/* Discount Badge */}
          {hasDiscount && (
            <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded-md text-sm font-semibold">
              {product.discount}% OFF
            </div>
          )}
          
          {/* Featured Badge */}
          {product.featured && (
            <div className="absolute top-2 left-2 bg-blue-600 text-white px-2 py-1 rounded-md text-sm font-semibold">
              Featured
            </div>
          )}
        </div>
        
        {/* Product Info */}
        <div className="p-4">
          {/* College Name */}
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
            {product.college?.name || 'College'}
          </p>
          
          {/* Product Name */}
          <Link to={`/products/${product.id}`}>
            <h3 className="text-lg font-semibold text-gray-900 mb-2 hover:text-blue-600 transition-colors line-clamp-2">
              {product.name}
            </h3>
          </Link>

          
          {/* Category */}
          <p className="text-sm text-gray-600 mb-3">
            {product.category === 'T_SHIRT' ? 'T-Shirt' : 'Hoodie'}
          </p>
          
          {/* Price */}
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xl font-bold text-gray-900">
              {formatCurrency ( ((100-product.discount)/100)*product.startingPrice) }
            </span>
            {hasDiscount && (
              <span className="text-sm text-gray-500 line-through">
                {formatCurrency(product.startingPrice)}
              </span>
            )}
          </div>
          
          {/* Stock Status */}
          {totalStock > 0 ? (
            <p className="text-sm text-green-600 mb-3">
              {totalStock < 10 
                ? `Only ${totalStock} left!` 
                : 'In Stock'}
            </p>
          ) : (
            <p className="text-sm text-red-600 mb-3">Out of Stock</p>
          )}
          
          {/* Add to Cart Button */}
          <button
            onClick={() => setShowModal(true)}
            disabled={totalStock === 0}
            className={`
              w-full py-2 px-4 rounded-lg font-medium transition-colors
              ${totalStock > 0
                ? 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }
            `}
          >
            {totalStock > 0 ? 'Add to Cart' : 'Out of Stock'}
          </button>
        </div>
      </div>
      
      {/* Add to Cart Modal */}
      <AddToCartModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        product={product}
      />
    </>
  );
};

export default ProductCard;